import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { T, STATUS, statusColor } from "../theme/tokens.js";
import { Panel, Empty, Chip, Notice } from "./ui/index.jsx";
import { mapModel, isDonePoint, stateLabel } from "../lib/geo.js";

/* Where the work is, and how much of it is done.
 *
 * One map per island, because a single map of the territory is mostly open
 * water with specks at the edges. Each point is a device at its own recorded
 * coordinates: filled green once it is complete, hollow while it is not, so an
 * island fills in as the crews work across it.
 *
 * The basemap is fetched from an outside tile service. On a network that
 * blocks it the tiles simply do not arrive, and the points still draw at their
 * true positions against the panel colour: a plainer map rather than no map.
 * That is why the points and the frame are drawn by this app and only the
 * background comes from anywhere else.
 */
/* The basemap.
 *
 * This was CARTO's light style, which now stamps API KEY REQUIRED across every
 * tile: their free tier ended and the tiles kept serving, watermarked, rather
 * than failing outright. OpenStreetMap's own tiles need no key and no account,
 * and their usage policy covers a tool this size comfortably. Attribution is a
 * condition of using them and is why the credit sits in the corner of every
 * map.
 *
 * They are drawn a good deal more colourfully than this app, so the layer is
 * desaturated in CSS. That is a filter on the tiles alone: the points, which
 * are the actual information, keep their full colour and sit clearly on top of
 * a quiet background. */
const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const QUIET_TILES = "grayscale(0.55) contrast(0.92) brightness(1.06)";

function IslandMap({ region, height = 320, onOpen, paint }) {
  const host = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const [tiles, setTiles] = useState("loading");

  useEffect(() => {
    if (!host.current || map.current) return undefined;
    const m = L.map(host.current, {
      zoomControl: true, attributionControl: true, scrollWheelZoom: false,
    });
    /* One successful tile is the only proof that the basemap arrived.
       Leaflet's "load" fires when the visible tiles have finished whether or
       not any of them succeeded, and a blocked network often answers with
       nothing at all rather than with an error, so what is counted here is
       tiles that actually loaded. */
    let arrived = 0;
    L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 19 })
      .on("tileload", () => { arrived += 1; setTiles("ok"); })
      .addTo(m);

    /* Applied to the tile pane rather than the map, so only the background is
       quietened and every marker drawn above it keeps its colour. */
    const pane = m.getPane("tilePane");
    if (pane) pane.style.filter = QUIET_TILES;
    map.current = m;

    const waited = setTimeout(() => { if (!arrived) setTiles("blocked"); }, 6000);

    return () => { clearTimeout(waited); m.remove(); map.current = null; };
  }, []);

  /* Points are redrawn rather than diffed. A tracker of a few hundred sites
     costs nothing to redraw, and diffing markers is where map code goes to
     die. */
  /* The click handler is held in a ref so passing a fresh one on every render
     does not tear down and redraw every marker on the island. */
  const open = useRef(onOpen);
  useEffect(() => { open.current = onOpen; }, [onOpen]);

  useEffect(() => {
    const m = map.current;
    if (!m || !region) return;

    if (layer.current) layer.current.remove();
    const group = L.layerGroup().addTo(m);
    layer.current = group;

    for (const p of region.points) {
      const { fill, ink } = paint(p);
      L.circleMarker([p.lat, p.lon], {
        radius: 6,
        color: ink,
        weight: 2,
        fillColor: fill,
        fillOpacity: fill === "#fff" ? 0.85 : 0.9,
        /* A dot on a map is the smallest thing on this screen to hit, so the
           cursor says it is worth aiming at. */
        className: "site-point",
      })
        .bindTooltip(
          `<b>${escapeHtml(p.label || "Site")}</b>` +
          (p.status ? `<br>${escapeHtml(stateLabel(p.status))}` : "") +
          `<br>${p.lat.toFixed(5)}, ${p.lon.toFixed(5)}` +
          "<br><i>Click to open the record</i>",
          { direction: "top" },
        )
        .on("click", () => open.current?.(p.label || "Site", [p.row]))
        .addTo(group);
    }

    if (region.bounds) m.fitBounds(region.bounds, { padding: [18, 18] });
  }, [region, paint]);

  return (
    <div>
      <div ref={host} style={{
        height, borderRadius: 4, border: `1px solid ${T.line}`,
        background: T.chip, zIndex: 0,
      }} />
      {tiles === "blocked" && (
        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>
          The background map could not load on this network. The points are still
          at their real coordinates, so the shape of the work is right even
          without the coastline behind it.
        </div>
      )}
    </div>
  );
}

const escapeHtml = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

/* A bar of how far along one island is, which is the number somebody asks for
   before they look at the map. */
function ProgressLine({ done, total, percent, onOpen }) {
  return (
    <div
      onClick={onOpen}
      title={onOpen ? "Open every site here" : undefined}
      style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
               cursor: onOpen ? "pointer" : "default" }}>
      <div style={{ flex: 1, height: 10, background: T.line + "88",
                    borderRadius: 5, overflow: "hidden" }}>
        <div style={{ width: `${percent ?? 0}%`, height: "100%",
                      background: STATUS.complete.fill }} />
      </div>
      <span style={{ fontSize: 12.5, color: T.soft, whiteSpace: "nowrap" }}>
        {done} of {total}{percent === null ? "" : `, ${percent}%`}
      </span>
    </div>
  );
}

/* Leaflet draws its markers into an SVG it owns, so the only way to say "this
   is worth clicking" is a rule against the class the marker carries. */
const POINT_CURSOR = ".site-point { cursor: pointer; }";

/* How a point is coloured, when nothing else is said: filled once it is
   finished, hollow while it is not. Two states, because that is all a pole
   has. A layer that moves through more than two gets given its own. */
const donePaint = (p) => (isDonePoint(p)
  ? { fill: STATUS.complete.fill, ink: STATUS.complete.ink }
  : { fill: "#fff", ink: STATUS.inProgress.ink });

const DONE_LEGEND = [
  { label: "Complete", fill: STATUS.complete.fill, ink: STATUS.complete.ink },
  { label: "Outstanding", fill: "#fff", ink: STATUS.inProgress.ink },
];

function Swatch({ fill, ink }) {
  return (
    <span style={{
      display: "inline-block", width: 9, height: 9, borderRadius: "50%",
      background: fill, border: `2px solid ${ink}`,
      marginRight: 6, verticalAlign: "-1px",
    }} />
  );
}

export default function SiteMap({
  columns, rows, title = "Where the work is", note, onDrill,
  statusKey, colorOf, legend, empty,
}) {
  const model = mapModel(columns, rows, statusKey === undefined ? {} : { statusKey });
  const paint = colorOf ?? donePaint;

  /* Everything on this panel opens what it counted, the same as every other
     figure in the app. A map that can only be looked at is a picture. */
  const open = (heading, sites) => {
    if (!onDrill || !sites?.length) return;
    onDrill({ title: heading, rows: sites.map((s) => s.row ?? s) });
  };
  const clickable = onDrill ? { cursor: "pointer" } : undefined;

  if (!model) {
    return (
      <Panel title={title}>
        <Empty text={empty ?? "This tracker has no coordinates. A column called Latitude and one called Longitude, with numbers in them, and the maps draw themselves."} />
      </Panel>
    );
  }

  return (
    <Panel title={title} accent={T.gold} right={
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={clickable}
          onClick={() => open("Complete", model.sites.filter(isDonePoint))}>
          <Chip color={STATUS.complete.ink}>{model.done} complete</Chip>
        </span>
        <span style={clickable}
          onClick={() => open("Outstanding", model.sites.filter((s) => !isDonePoint(s)))}>
          <Chip color={STATUS.inProgress.ink}>{model.total - model.done} outstanding</Chip>
        </span>
        {model.percent !== null && (
          <span style={clickable} onClick={() => open("Every site", model.sites)}>
            <Chip color={T.navy}>{model.percent}%</Chip>
          </span>
        )}
      </div>
    }>
      <style>{POINT_CURSOR}</style>

      {model.missing > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Notice>
            {model.placed} of {model.total} sites have coordinates recorded and are
            drawn. The other {model.missing} are counted in every figure here and
            appear on the map the week somebody records where they are.
          </Notice>
        </div>
      )}

      <div style={{ display: "grid", gap: 16,
                    gridTemplateColumns: model.regions.length > 1
                      ? "repeat(auto-fit, minmax(330px, 1fr))" : "minmax(0, 1fr)" }}>
        {model.regions.map((r) => (
          <div key={r.name} style={{ minWidth: 0 }}>
            <div
              onClick={() => open(r.name, r.all)}
              title={onDrill ? `Open all ${r.total} sites on ${r.name}` : undefined}
              style={{ fontSize: 13.5, fontWeight: 700, color: T.navy, marginBottom: 6,
                       ...(clickable ?? {}) }}>
              {r.name}
            </div>
            <ProgressLine done={r.done} total={r.total} percent={r.percent}
              onOpen={onDrill ? () => open(r.name, r.all) : undefined} />
            {r.points.length === 0 ? (
              /* An island nobody has reached yet. Drawing an empty frame of
                 open water would say less than one honest sentence. */
              <div style={{
                height: model.regions.length > 1 ? 300 : 420, borderRadius: 4,
                border: `1px dashed ${T.line}`, background: T.chip + "66",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: 20, textAlign: "center",
              }}>
                <span style={{ fontSize: 12.5, color: T.soft, maxWidth: 260, lineHeight: 1.6 }}>
                  {r.total} site{r.total === 1 ? "" : "s"} planned here and none
                  surveyed yet, so there is nothing to place on a map. It draws
                  itself as soon as the first coordinates arrive.
                </span>
              </div>
            ) : (
              <IslandMap region={r} paint={paint}
                height={model.regions.length > 1 ? 300 : 420}
                onOpen={onDrill ? (heading, records) =>
                  onDrill({ title: heading, rows: records }) : undefined} />
            )}
            {r.awaiting > 0 && r.points.length > 0 && (
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>
                {r.awaiting} more planned here, not yet surveyed.
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 12, color: T.soft, marginTop: 12, lineHeight: 1.6 }}>
        {(legend ?? DONE_LEGEND).map((l, i) => (
          <span key={l.label} style={{ marginLeft: i ? 14 : 0, whiteSpace: "nowrap" }}>
            <Swatch fill={l.fill} ink={l.ink} />{l.label}
          </span>
        ))}
        .{" "}
        {onDrill
          ? "Click a point, an island name or a progress bar to open the records behind it."
          : "Hover a point for its id and status."}
        {note ? ` ${note}` : ""}
      </div>
    </Panel>
  );
}
